import React, { useRef, useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { googleReviews } from "./reviewsConstants";

const getTimeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  const diffInDays = Math.floor(diffInSeconds / (3600 * 24));
  
  if (diffInDays < 1) return "Today";
  if (diffInDays < 7) {
    return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInDays < 30) {
    return diffInWeeks === 1 ? "1 week ago" : `${diffInWeeks} weeks ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInDays < 365) {
    return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
  }
  
  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
};

const TestimonialsSection = () => {
  const sliderRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  const testimonials = googleReviews;

  const updateArrowVisibility = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollWidth - scrollLeft - clientWidth > 5);
    }
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -350, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 350, behavior: "smooth" });
    }
  };

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      updateArrowVisibility();
      slider.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);
    }
    return () => {
      if (slider) {
        slider.removeEventListener("scroll", updateArrowVisibility);
        window.removeEventListener("resize", updateArrowVisibility);
      }
    };
  }, [testimonials]);

  return (
    <section className="section testimonials section-white" id="testimonials">
      <div className="container">
        <div className="section-header reveal-triggered fade-up active" data-aos="fade-up">
          <h2 style={{ color: "var(--text-primary)", fontWeight: 800 }}>What Students and Parents Say <span style={{ color: "#6366f1" }}>About Practido</span></h2>
          <p>Thousands of students are already developing future-ready skills through project-based learning and innovation programs.</p>
        </div>

        <div className="testimonials-slider-wrapper" style={{ position: "relative" }} data-aos="zoom-in" data-aos-delay="100">
          {showLeftArrow && (
            <button className="courses-scroll-btn left" onClick={scrollLeft} aria-label="Scroll left" style={{ position: "absolute", left: "-20px", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}>
              <ChevronLeft size={24} />
            </button>
          )}

          <div 
            className="testimonials-grid" 
            ref={sliderRef}
            style={{
              display: "flex",
              overflowX: "auto",
              scrollBehavior: "smooth",
              gap: "24px",
              padding: "10px",
              scrollbarWidth: "none", // Hide scrollbar for Firefox
              msOverflowStyle: "none" // Hide scrollbar for IE/Edge
            }}
          >
            {testimonials.map((test, index) => (
              <div
                key={index}
                className={`premium-card testimonial-card reveal-triggered zoom-in active`}
                style={{ 
                  minWidth: "320px", 
                  flex: "0 0 auto", 
                  maxWidth: "350px",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <div className="testimonial-header" style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                  {test.avatarImg && !imageErrors[index] ? (
                     <div className="testimonial-avatar" style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden" }}>
                        <img src={test.avatarImg} alt={test.name} onError={() => handleImageError(index)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                     </div>
                  ) : test.avatarInitial ? (
                    <div className="testimonial-avatar" style={{ backgroundColor: test.avatarBg, width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "bold", fontSize: "18px" }}>
                      {test.avatarInitial}
                    </div>
                  ) : (
                    <div className="testimonial-avatar" style={{ backgroundColor: test.avatarBg }}>
                      <img src={test.avatar} alt={`${test.name} - student or parent review at Practido`} loading="lazy" />
                    </div>
                  )}
                  <div className="testimonial-info">
                    <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>{test.name}</h4>
                    <p style={{ margin: 0, fontSize: "12px", color: "gray" }}>{test.date ? getTimeAgo(test.date) : (test.time || test.role)}</p>
                  </div>
                </div>
                <div className="testimonial-stars" style={{ display: "flex", gap: "2px", marginBottom: "12px" }}>
                  {Array.from({ length: test.rating || 5 }).map((_, i) => (
                    <Star key={i} size={16} fill="#fbbf24" stroke="#fbbf24" strokeWidth={2} />
                  ))}
                </div>
                <p className="testimonial-text" style={{ fontSize: "14px", lineHeight: "1.5", color: "#4b5563" }}>{test.text}</p>
              </div>
            ))}
          </div>

          {showRightArrow && (
            <button className="courses-scroll-btn right" onClick={scrollRight} aria-label="Scroll right" style={{ position: "absolute", right: "-20px", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}>
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{__html: `
        .testimonials-grid::-webkit-scrollbar { display: none; }
      `}} />
    </section>
  );
};

export default TestimonialsSection;
